import { TournamentHeader } from "./TournamentHeader";
import { Tournament } from "@portal-frontend-ssr/blast-data-api";
import { GameId } from "@portal-frontend-ssr/blast-api";
import { AdaptiveLink } from "@portal-frontend-ssr/ui";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { formatTournamentDate } from "~/shared/components/ArticlePage/components/ArticlePageSidebar/components/shared/components/TournamentWidget/helpers/formatTournamentDate";
import { MatchPageLinkWrapper } from "~/components/MatchPageLinkWrapper";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";
import { ClientOnly } from "remix-utils/client-only";
import { generateTournamentLink } from "~/shared/helpers/tournaments/generateTournamentLink";

interface PastTournamentsProps {
  gameId: GameId;
  tournament: Tournament;
}

export const PastTournaments = ({ gameId, tournament }: PastTournamentsProps) => {
  const keyMatches = tournament.keyMatches;

  if (!keyMatches) {
    return null;
  }

  const tournamentLink = generateTournamentLink({ gameId, tournamentId: tournament.id });

  return (
    <div className="flex select-none flex-col gap-4 rounded-small bg-background-95 p-3" key={tournament.id}>
      <AdaptiveLink to={tournamentLink} className="text-foreground-100 transition-all hover:text-foreground-95">
        <TournamentHeader tournament={tournament} />
      </AdaptiveLink>
      {keyMatches.map((match, index) => {
        const matchPageUrl = generateMatchPageUrl({
          game: gameId,
          tournamentName: match.tournament.name,
          matchUuid: match.id,
          matchName: match.name,
          teamAName: match.teamA?.shortName,
          teamBName: match.teamB?.shortName,
        });

        return (
          <div className="flex flex-col gap-1" key={index}>
            <div className="flex justify-between font-style-label-l4">
              <span>{match.name}</span>
              <ClientOnly>
                {() => <span>{formatTournamentDate({ startDate: match.scheduledAt, type: "short" })}</span>}
              </ClientOnly>
            </div>
            <MatchPageLinkWrapper
              key={match.id}
              condition={(gameId === "cs" || gameId === "dota" || gameId === "rl") && matchPageUrl !== undefined}
              wrapper={(children) => <AdaptiveLink to={matchPageUrl ?? ""}>{children}</AdaptiveLink>}
            >
              <div className="flex justify-center gap-2 rounded-small bg-background-85 p-3 transition-all hover:bg-background-80">
                <div className="flex flex-1 items-center justify-end gap-2 overflow-hidden">
                  <span className="truncate text-right font-style-body-b3">{match.teamA?.name}</span>
                  <img
                    src={generateTeamLogoSrc({ id: match.teamA?.id ?? "", height: 20 })}
                    alt={match.teamA?.name}
                    className="size-5 object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center gap-1 rounded-small bg-background-100 px-2 py-1 font-style-label-l3">
                  <span>{match.teamAScore}</span>
                  <span>-</span>
                  <span>{match.teamBScore}</span>
                </div>
                <div className="flex flex-1 items-center justify-start gap-2 overflow-hidden">
                  <img
                    src={generateTeamLogoSrc({ id: match.teamB?.id ?? "", height: 20 })}
                    alt={match.teamB?.name}
                    className="size-5 object-contain"
                    loading="lazy"
                  />
                  <span className="truncate text-left font-style-body-b3">{match.teamB?.name}</span>
                </div>
              </div>
            </MatchPageLinkWrapper>
          </div>
        );
      })}
    </div>
  );
};
